import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { findCanonicalSuperviseeIds } from '../lib/supervision/supervisionRelationships';
import { useCurrentUserPractitioner } from './useCurrentUserPractitioner';

export interface SupervisedPractitionerIds {
  ids: string[] | undefined;
  loading: boolean;
}

export function shouldSearchSupervisedPractitioners(input: {
  isSupervisor: boolean;
  rbacReady: boolean;
  practitionerLoading: boolean;
  viewerId: string | undefined;
}): boolean {
  return input.isSupervisor && input.rbacReady && !input.practitionerLoading && !!input.viewerId;
}

/** Render result for `useSupervisedPractitionerIds`. Failed and non-supervisor states stay closed (`ids` undefined). */
export function supervisedPractitionerIdsView(input: {
  isSupervisor: boolean;
  rbacReady: boolean;
  practitionerLoading: boolean;
  viewerId: string | undefined;
  phase: 'pending' | 'ready' | 'error';
  ids: string[] | undefined;
}): SupervisedPractitionerIds {
  if (!input.isSupervisor) return { ids: undefined, loading: false };
  if (!input.rbacReady || input.practitionerLoading || !input.viewerId || input.phase === 'pending') {
    return { ids: undefined, loading: true };
  }
  if (input.phase === 'error') return { ids: undefined, loading: false };
  return { ids: input.ids, loading: false };
}

/**
 * Live supervisee ids for a supervisor. Other roles do not search.
 * `ids` stays undefined until the lookup succeeds; a failed search stays undefined
 * so chart authorization closes instead of treating an empty list as "unassigned episodes only".
 */
export function useSupervisedPractitionerIds(): SupervisedPractitionerIds {
  const medplum = useMedplum();
  const { role, ready: rbacReady } = usePermissions();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  const isSupervisor = role === 'supervisor';
  const viewerId = practitioner?.id;

  const [ids, setIds] = useState<string[] | undefined>(undefined);
  const [phase, setPhase] = useState<'pending' | 'ready' | 'error'>('pending');

  useEffect(() => {
    if (!shouldSearchSupervisedPractitioners({
      isSupervisor,
      rbacReady,
      practitionerLoading,
      viewerId,
    })) {
      setIds(undefined);
      setPhase('pending');
      return;
    }

    let cancelled = false;
    setPhase('pending');
    (async () => {
      try {
        medplum.invalidateSearches('Practitioner');
        const all = await medplum.searchResources('Practitioner', { _count: '500' });
        if (cancelled) return;
        setIds(findCanonicalSuperviseeIds(all, viewerId!));
        setPhase('ready');
      } catch {
        if (!cancelled) {
          setIds(undefined);
          setPhase('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, isSupervisor, rbacReady, practitionerLoading, viewerId]);

  return supervisedPractitionerIdsView({
    isSupervisor,
    rbacReady,
    practitionerLoading,
    viewerId,
    phase,
    ids,
  });
}
